import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from 'react-toastify';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  const fetchProduct = useCallback(async () => {
    try {
      const response = await axios.get(`/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      toast.error('Failed to fetch product details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
    setActiveImage(0);
  }, [fetchProduct]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-6">
        <div className="max-w-4xl mx-auto animate-pulse flex flex-col gap-6">
          <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden md:flex h-[400px]">
            <div className="md:w-2/5 bg-gray-200" />
            <div className="md:w-3/5 p-7 flex flex-col gap-4">
              <div className="h-6 bg-gray-200 rounded w-20" />
              <div className="h-8 bg-gray-200 rounded w-44" />
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
              <div className="h-10 bg-gray-200 rounded w-28 mt-4" />
              <div className="flex gap-4 mt-auto">
                <div className="h-12 bg-gray-200 rounded-xl flex-1" />
                <div className="h-12 bg-gray-200 rounded-xl flex-1" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-3xl mb-3">🔍</p>
          <p className="text-gray-700 font-medium mb-1">Product not found</p>
          <p className="text-gray-400 text-sm mb-5">It may have been removed or the link is incorrect.</p>
          <Link to="/products" className="text-sm font-medium text-blue-600 hover:underline">
            ← Back to products
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : null;
  const inStock = product.stock === undefined || product.stock > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-6">
          <Link to="/products" className="hover:text-gray-600 transition">Products</Link>
          <span>/</span>
          <span className="text-gray-600">{product.name}</span>
        </div>

        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden md:flex">

          {/* Image gallery */}
          <div className="md:w-2/5">
            {images ? (
              <img
                src={images[activeImage]}
                alt={product.name}
                className="w-full h-72 md:h-full object-cover"
              />
            ) : (
              <div className="w-full h-72 md:h-full bg-gray-50 flex items-center justify-center">
                <span className="text-gray-300 text-sm">No image</span>
              </div>
            )}
            {images && images.length > 1 && (
              <div className="flex gap-2 p-3 bg-gray-50 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={img + i}
                    onClick={() => setActiveImage(i)}
                    className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition ${
                      activeImage === i ? 'border-blue-500' : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="md:w-3/5 p-7 flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-3">
              {product.category && (
                <span className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full capitalize">
                  {product.category}
                </span>
              )}
              {!inStock && (
                <span className="bg-orange-50 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  Out of stock
                </span>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
            
            {/* Star rating row */}
            <div className="flex items-center gap-1.5 mb-4">
              <div className="flex text-amber-400">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className="text-sm">★</span>
                ))}
              </div>
              <span className="text-xs text-gray-500 font-medium">
                ({product._id ? (4.5 + (product._id.charCodeAt(0) % 5) * 0.1).toFixed(1) : '4.8'}) • {product._id ? (10 + (product._id.charCodeAt(product._id.length - 1) % 40)) : 15} reviews
              </span>
            </div>

            <p className="text-sm text-gray-500 leading-relaxed mb-5">{product.description}</p>

            <p className="text-3xl font-bold text-blue-600 mb-6">₹{product.price}</p>

            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              <button
                disabled={!inStock}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium py-3 rounded-xl transition"
              >
                {inStock ? 'Add to cart' : 'Out of stock'}
              </button>
              <Link
                to="/book-service"
                className="flex-1 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 text-sm font-medium py-3 rounded-xl transition text-center"
              >
                Need a repair instead?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;